// Date and Django-duration helpers shared by the pages.

/** Parse a Django duration string ("HH:MM:SS", "1 02:30:00", "01:30:00.500000") to seconds. */
export function parseDurationToSeconds(duration: string): number {
  if (!duration) return 0;
  let days = 0;
  let rest = duration.trim();

  const dayMatch = rest.match(/^(\d+)\s+/);
  if (dayMatch) {
    days = parseInt(dayMatch[1], 10);
    rest = rest.slice(dayMatch[0].length);
  }

  const parts = rest.split(':').map((p) => parseFloat(p));
  if (parts.some(Number.isNaN)) return 0;

  let seconds = 0;
  if (parts.length === 3) seconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
  else if (parts.length === 2) seconds = parts[0] * 60 + parts[1];
  else if (parts.length === 1) seconds = parts[0];

  return days * 86400 + seconds;
}

/** Format seconds as a compact human duration, e.g. "2h 30m" or "45m". */
export function formatDuration(totalSeconds: number): string {
  const seconds = Math.round(totalSeconds);
  if (seconds <= 0) return '0m';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  if (hours && minutes) return `${hours}h ${minutes}m`;
  if (hours) return `${hours}h`;
  return `${minutes}m`;
}

export function formatDurationString(duration: string): string {
  return formatDuration(parseDurationToSeconds(duration));
}

/** Build the "HH:MM:00" payload the API expects from hour/minute inputs. */
export function toDurationPayload(hours: number, minutes: number): string {
  const h = String(Math.max(0, Math.floor(hours))).padStart(2, '0');
  const m = String(Math.max(0, Math.floor(minutes))).padStart(2, '0');
  return `${h}:${m}:00`;
}

/** Split seconds into {hours, minutes} for form inputs. */
export function secondsToHoursMinutes(totalSeconds: number): { hours: number; minutes: number } {
  const seconds = Math.max(0, Math.round(totalSeconds));
  return { hours: Math.floor(seconds / 3600), minutes: Math.round((seconds % 3600) / 60) };
}

/** "2026-06-12" → "Jun 12, 2026" (in the user's locale). */
export function formatDate(isoDate: string): string {
  if (!isoDate) return '';
  const date = new Date(`${isoDate.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return isoDate;
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** ISO datetime → "Jun 12, 2026, 4:30 PM" (in the user's locale). */
export function formatDateTime(iso: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** ISO datetime → value for an <input type="datetime-local"> in local time. */
export function toDateTimeLocalValue(iso?: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Today as "YYYY-MM-DD" in local time (for date input defaults). */
export function todayISODate(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function isOverdue(dueDate?: string | null, completed?: boolean): boolean {
  if (!dueDate || completed) return false;
  const due = new Date(dueDate);
  return !Number.isNaN(due.getTime()) && due.getTime() < Date.now();
}

/** Friendly relative label for thread lists, e.g. "Today", "Yesterday", "Jun 3". */
export function formatRelativeDay(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(date)) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return date.toLocaleDateString(undefined, { weekday: 'long' });
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// ---------------------------------------------------------------------------
// Time-bucketing helpers (used by the Analytics screen)
//
// All math is done in *local* time and keyed off "YYYY-MM-DD" entry dates, the
// same way the rest of the app interprets `entry_date`. Weeks start on Monday.
// ---------------------------------------------------------------------------

export type Granularity = 'day' | 'week' | 'month' | 'year';

const pad2 = (n: number) => String(n).padStart(2, '0');

/** Parse a "YYYY-MM-DD" string as a local Date at midnight. */
export function parseISODate(isoDate: string): Date {
  return new Date(`${isoDate.slice(0, 10)}T00:00:00`);
}

/** Format a local Date as "YYYY-MM-DD". */
export function toISODate(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/** Monday-anchored start of the week containing `d`. */
function startOfWeek(d: Date): Date {
  const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const offset = (date.getDay() + 6) % 7; // Mon=0 … Sun=6
  date.setDate(date.getDate() - offset);
  return date;
}

/** Normalize a date down to the start of its bucket for the given granularity. */
export function startOfBucket(d: Date, g: Granularity): Date {
  if (g === 'year') return new Date(d.getFullYear(), 0, 1);
  if (g === 'month') return new Date(d.getFullYear(), d.getMonth(), 1);
  if (g === 'week') return startOfWeek(d);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Advance a bucket-start date by exactly one bucket. */
function nextBucket(d: Date, g: Granularity): Date {
  if (g === 'year') return new Date(d.getFullYear() + 1, 0, 1);
  if (g === 'month') return new Date(d.getFullYear(), d.getMonth() + 1, 1);
  if (g === 'week') return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 7);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
}

/** Stable key for the bucket a date falls into, e.g. "2026", "2026-06", "2026-06-08". */
export function bucketKeyFor(d: Date, g: Granularity): string {
  const start = startOfBucket(d, g);
  if (g === 'year') return `${start.getFullYear()}`;
  if (g === 'month') return `${start.getFullYear()}-${pad2(start.getMonth() + 1)}`;
  return toISODate(start); // day & week both keyed by their start date
}

/** Short x-axis label for a bucket key. */
export function bucketLabelFor(key: string, g: Granularity): string {
  if (g === 'year') return key;
  if (g === 'month') {
    const [y, m] = key.split('-');
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(undefined, { month: 'short' });
  }
  return parseISODate(key).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Verbose label for tooltips, e.g. "Mon, Jun 8", "Week of Jun 8", "June 2026", "2026". */
export function bucketFullLabelFor(key: string, g: Granularity): string {
  if (g === 'year') return key;
  if (g === 'month') {
    const [y, m] = key.split('-');
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(undefined, {
      month: 'long',
      year: 'numeric',
    });
  }
  const date = parseISODate(key);
  if (g === 'week') {
    return `Week of ${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
  }
  return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

/**
 * Ordered list of bucket keys spanning [from, to] inclusive. Empty buckets are
 * included so gaps in the timeline are visible. `limit` keeps the most recent
 * buckets when a range would produce more than the chart can usefully show.
 */
export function enumerateBuckets(from: Date, to: Date, g: Granularity, limit = 90): string[] {
  const keys: string[] = [];
  let cursor = startOfBucket(from, g);
  const end = startOfBucket(to, g);
  let guard = 0;
  while (cursor.getTime() <= end.getTime() && guard < 4000) {
    keys.push(bucketKeyFor(cursor, g));
    cursor = nextBucket(cursor, g);
    guard += 1;
  }
  return keys.length > limit ? keys.slice(keys.length - limit) : keys;
}

/** Sensible default [from, to] ISO date range for a granularity (ending today). */
export function defaultRangeFor(g: Granularity): { from: string; to: string } {
  const today = new Date();
  const to = toISODate(today);
  let from: Date;
  if (g === 'day') {
    from = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 13); // 14 days
  } else if (g === 'week') {
    const ws = startOfWeek(today);
    from = new Date(ws.getFullYear(), ws.getMonth(), ws.getDate() - 7 * 11); // 12 weeks
  } else if (g === 'month') {
    from = new Date(today.getFullYear(), today.getMonth() - 11, 1); // 12 months
  } else {
    from = new Date(today.getFullYear() - 4, 0, 1); // 5 years
  }
  return { from: toISODate(from), to };
}
